import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Shield,
  Upload,
  FileText,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  CompletenessResult,
  canUserOverride,
  applyOverride,
} from '@/hooks/usePacketCompleteness';
import { format, parseISO } from 'date-fns';

interface PacketCompletenessCardProps {
  caseId: string;
  completeness: CompletenessResult;
  logActivity: (action: string, details?: Record<string, unknown>) => Promise<void>;
  onRefetch: () => void;
  onDocumentUpload?: (docType: string) => void;
}

export function PacketCompletenessCard({
  caseId,
  completeness,
  logActivity,
  onRefetch,
  onDocumentUpload,
}: PacketCompletenessCardProps) {
  const [canOverride, setCanOverride] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    canUserOverride().then(setCanOverride);
  }, []);

  const handleOverride = async () => {
    if (!overrideReason.trim()) {
      toast.error('Override reason is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await applyOverride(caseId, overrideReason, logActivity);
      toast.success('Override applied successfully');
      setShowOverrideDialog(false);
      setOverrideReason('');
      onRefetch();
    } catch (error) {
      console.error('Error applying override:', error);
      toast.error('Failed to apply override');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = () => {
    switch (completeness.status) {
      case 'READY':
        return <CheckCircle2 className="w-5 h-5 text-primary" />;
      case 'OVERRIDDEN':
        return <Shield className="w-5 h-5 text-amber-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusBadge = () => {
    switch (completeness.status) {
      case 'READY':
        return (
          <Badge className="bg-primary/10 text-primary border-primary/30">
            Ready to Submit
          </Badge>
        );
      case 'OVERRIDDEN':
        return (
          <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30">
            Override Applied
          </Badge>
        );
      default:
        return (
          <Badge className="bg-red-500/10 text-red-500 border-red-500/30">
            Incomplete
          </Badge>
        );
    }
  };

  const getProgressColor = () => {
    if (completeness.score >= 100) return 'bg-primary';
    if (completeness.score >= 75) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <>
      <Card className={cn(
        "border-2",
        completeness.status === 'READY' && "border-primary/30",
        completeness.status === 'OVERRIDDEN' && "border-amber-500/30",
        completeness.status === 'INCOMPLETE' && "border-red-500/30"
      )}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2">
              {getStatusIcon()}
              Packet Completeness
            </span>
            {getStatusBadge()}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Score Meter */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Score</span>
              <span className="font-mono font-bold text-lg">{completeness.score}%</span>
            </div>
            <div className="relative h-3 bg-muted rounded-full overflow-hidden">
              <div
                className={cn("h-full transition-all duration-500", getProgressColor())}
                style={{ width: `${completeness.score}%` }}
              />
            </div>
          </div>

          {/* Checklist Items */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Checklist
            </p>
            <div className="space-y-1.5">
              {completeness.items.map((item) => (
                <div
                  key={item.id}
                  className={cn(
                    "flex items-center justify-between p-2 rounded-md text-sm",
                    item.isComplete ? "bg-emerald-500/10" : "bg-muted/50"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {item.isComplete ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className={cn(item.isComplete && "text-muted-foreground")}>
                      {item.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs font-mono">
                      {item.weight}%
                    </Badge>
                    {!item.isComplete && onDocumentUpload && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2"
                        onClick={() => onDocumentUpload(item.code)}
                      >
                        <Upload className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Override info if applied */}
          {completeness.isOverridden && completeness.overrideReason && (
            <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-amber-500 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-500">Override Applied</p>
                  <p className="text-muted-foreground mt-1">{completeness.overrideReason}</p>
                  {completeness.overrideAt && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(parseISO(completeness.overrideAt), 'MMM d, yyyy h:mm a')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {completeness.status === 'INCOMPLETE' && canOverride && (
            <Button
              variant="outline"
              size="sm"
              className="w-full border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
              onClick={() => setShowOverrideDialog(true)}
            >
              <Shield className="w-4 h-4 mr-2" />
              Override and Submit
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Override Incomplete Packet
            </DialogTitle>
            <DialogDescription>
              You are about to submit a case with an incomplete packet. This action will be logged for audit purposes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Missing Items Summary */}
            <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30">
              <p className="text-sm font-medium text-red-500 mb-2">Missing Items:</p>
              <ul className="text-sm space-y-1">
                {completeness.missingItems.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-muted-foreground">
                    <XCircle className="w-3 h-3 text-red-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Override Reason */}
            <div className="space-y-2">
              <Label htmlFor="override-reason">
                Override Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="override-reason"
                placeholder="Explain why this case can proceed without complete documentation..."
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                This reason will be recorded in the audit log.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowOverrideDialog(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleOverride}
              disabled={isSubmitting || !overrideReason.trim()}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Shield className="w-4 h-4 mr-2" />
              )}
              Confirm Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
